import React, { useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { useFetch } from "../lib/useApi";
import { Loader } from "./PageHead";

const DAY_OPTIONS = [7, 14, 30, 60, 90];

function TinyTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card pad" style={{ boxShadow: "var(--shadow-md)", padding: 10, fontSize: 12.5 }}>
      <div className="t-strong" style={{ marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", gap: 8, alignItems: "center", color: p.color }}>
          <span style={{ width: 8, height: 8, borderRadius: 8, background: p.color, display: "inline-block" }} />
          {p.name}: <b>{p.value}</b>
        </div>
      ))}
    </div>
  );
}

// `receivedColor`/`calibratedColor`/`dayOptions`/`showBacklog`/`title` are
// optional — Job Flow Board's usage (no props) is byte-identical to before.
// The Technical Dashboard passes its own gold colour scheme + 7d/30d/12mo
// periods + a Net Backlog figure.
export function ThroughputChart({ receivedColor, calibratedColor, dayOptions, showBacklog, title }) {
  const [mode, setMode] = useState("daily");
  const opts = dayOptions || DAY_OPTIONS;
  const [days, setDays] = useState(opts[0]);
  const { data, loading } = useFetch("/dashboard/throughput", { days }, [days]);
  const recvColor = receivedColor || "var(--blue)";
  const calColor = calibratedColor || "var(--green)";

  const series = mode === "daily" ? data?.daily : data?.monthly;
  const totals = (series || []).reduce(
    (acc, r) => ({ received: acc.received + r.received, calibrated: acc.calibrated + r.calibrated }),
    { received: 0, calibrated: 0 }
  );
  const netBacklog = totals.received - totals.calibrated;

  return (
    <div className="card pad" data-testid="throughput-chart-card">
      <div className="sec-h">
        <h3>{title || "Workshop throughput"}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {mode === "daily" && (
            <select
              className="input"
              data-testid="throughput-day-range-select"
              style={{ width: "auto", padding: "6px 9px", fontSize: 12.5 }}
              value={days}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 365) setMode("monthly"); else setDays(v);
              }}
            >
              {opts.map((d) => (
                <option key={d} value={d} data-testid={`throughput-day-option-${d}`}>{d >= 365 ? "Last 12 months" : `Last ${d} days`}</option>
              ))}
            </select>
          )}
          {mode === "monthly" && dayOptions && (
            <button type="button" className="btn ghost sm" data-testid="throughput-back-to-days"
              onClick={() => setMode("daily")}>
              ← Back to days
            </button>
          )}
          {!dayOptions && (
            <div style={{ display: "flex", background: "var(--card-2)", borderRadius: 8, padding: 2, gap: 2 }}>
              <button
                type="button"
                data-testid="throughput-tab-daily"
                className={`btn sm ${mode === "daily" ? "gold" : "ghost"}`}
                style={{ border: mode === "daily" ? "none" : "1px solid transparent" }}
                onClick={() => setMode("daily")}
              >
                Daily
              </button>
              <button
                type="button"
                data-testid="throughput-tab-monthly"
                className={`btn sm ${mode === "monthly" ? "gold" : "ghost"}`}
                style={{ border: mode === "monthly" ? "none" : "1px solid transparent" }}
                onClick={() => setMode("monthly")}
              >
                Monthly (12mo)
              </button>
            </div>
          )}
        </div>
      </div>

      {loading || !series ? (
        <Loader />
      ) : series.length === 0 ? (
        <div className="t-mut" style={{ fontSize: 13, padding: "20px 0" }}>No activity recorded in this period.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 20, marginBottom: 4, fontSize: 12.5, flexWrap: "wrap" }}>
            <span className="t-mut">
              Received: <b className="t-strong" style={{ color: recvColor }} data-testid="throughput-total-received">{totals.received}</b>
            </span>
            <span className="t-mut">
              Calibrated: <b className="t-strong" style={{ color: calColor }} data-testid="throughput-total-calibrated">{totals.calibrated}</b>
            </span>
            {showBacklog && (
              <span className="t-mut">
                Net backlog: <b className="t-strong" style={{ color: netBacklog > 0 ? "var(--red)" : "var(--green)" }} data-testid="throughput-net-backlog">
                  {netBacklog > 0 ? "+" : ""}{netBacklog}
                </b>
              </span>
            )}
          </div>
          {mode === "daily" && (
            <div className="t-mut" style={{ fontSize: 11, marginBottom: 10 }}>
              Zero-activity days (e.g. weekends) are hidden from the chart.
            </div>
          )}
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="var(--line)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--text-3)" }}
                interval={mode === "daily" && series.length > 12 ? Math.ceil(series.length / 12) : 0}
                axisLine={{ stroke: "var(--line-2)" }}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--text-3)" }} axisLine={false} tickLine={false} />
              <Tooltip content={<TinyTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12.5 }}
                payload={[
                  { value: "Received", type: "line", color: recvColor },
                  { value: "Calibrated", type: "line", color: calColor },
                ]}
              />
              <Line type="monotone" dataKey="received" name="Received" stroke={recvColor} strokeWidth={2.2} dot={mode === "monthly"} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="calibrated" name="Calibrated" stroke={calColor} strokeWidth={2.2} dot={mode === "monthly"} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
